import { inboxCount, inboxPage } from "@/lib/inbox-response"
import { apiSlice as api } from "@/store/rtk-api/apiSlice"

export type NotificationChannel = "IN_APP" | "EMAIL" | "SMS"

export const NOTIFICATION_CHANNELS: NotificationChannel[] = ["IN_APP", "EMAIL", "SMS"]

export interface ResolvedChannelState {
  available: boolean
  enabled: boolean
  overridden: boolean
}

export interface ResolvedEventPrefs {
  eventId: string
  key: string
  category: string
  description: string
  configurable: boolean
  channels: Record<NotificationChannel, ResolvedChannelState>
}

export interface PreferenceOverrideEntry {
  eventId: string
  channel: NotificationChannel
  enabled: boolean
}

export interface PreferenceResetEntry {
  eventId: string
  channel?: NotificationChannel
}

export interface NotificationDeepLink {
  type: string
  params: Record<string, string>
}

export interface NotificationItem {
  id: string
  eventKey: string
  title: string
  body: string
  deepLink: NotificationDeepLink | null
  readAt: string | null
  createdAt: string
}

export interface NotificationsPage {
  items: NotificationItem[]
  nextCursor: string | null
  unreadCount: number
}

export interface PhoneStatus {
  phoneMasked: string | null
  phoneVerifiedAt: string | null
}

const notificationsAPI = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Resolved preference matrix (event defaults merged with the user's
     * overrides) — the client never re-implements resolution.
     */
    getNotificationPreferences: builder.query<ResolvedEventPrefs[], void>({
      query: () => ({
        url: "/v1/user/notification-preferences",
        method: "GET",
      }),
      transformResponse: (response: { success: boolean; preferences: ResolvedEventPrefs[] }) =>
        response?.preferences ?? [],
      providesTags: ["NotificationPreferences"],
    }),

    /** Bulk upsert of per-event/per-channel overrides. */
    updateNotificationPreferences: builder.mutation<
      { success: boolean; error?: string },
      { preferences: PreferenceOverrideEntry[] }
    >({
      query: (data) => ({
        url: "/v1/user/notification-preferences",
        method: "PUT",
        data,
      }),
      invalidatesTags: ["NotificationPreferences"],
    }),

    /** Tri-state reset: deletes override rows so events inherit defaults again. */
    resetNotificationPreferences: builder.mutation<
      { success: boolean; error?: string },
      { resets: PreferenceResetEntry[] }
    >({
      query: (data) => ({
        url: "/v1/user/notification-preferences",
        method: "DELETE",
        data,
      }),
      invalidatesTags: ["NotificationPreferences"],
    }),

    getNotifications: builder.query<NotificationsPage, { cursor?: string; limit?: number; scope?: string } | void>({
      query: (params) => ({
        url: "/v1/user/notifications",
        method: "GET",
        params: params ? { cursor: params.cursor, limit: params.limit } : undefined,
        suppressErrorToast: true,
        parseResponse: (response) => inboxPage(response, params?.cursor, params?.limit),
      }),
      providesTags: ["Notifications"],
    }),
    getUnreadNotificationCount: builder.query<number, string | void>({
      query: () => ({
        url: "/v1/user/notifications/unread-count",
        method: "GET",
        suppressErrorToast: true,
        parseResponse: inboxCount,
      }),
      providesTags: ["Notifications"],
    }),

    markNotificationsRead: builder.mutation<{ success: boolean; updated: number }, { ids: string[] }>({
      query: (data) => ({
        url: "/v1/user/notifications/mark-read",
        method: "POST",
        rejectFailureResponse: true,
        data,
      }),
      invalidatesTags: ["Notifications"],
    }),

    markAllNotificationsRead: builder.mutation<{ success: boolean; updated: number }, void>({
      query: () => ({
        url: "/v1/user/notifications/mark-all-read",
        method: "POST",
        rejectFailureResponse: true,
      }),
      invalidatesTags: ["Notifications"],
    }),

    /** Keeps notification rendering aligned with the language selected in the portal. */
    updatePreferredLocale: builder.mutation<{ success: boolean }, { preferredLocale: "en" | "es" }>({
      query: (data) => ({
        url: "/v1/user",
        method: "PATCH",
        data,
        suppressErrorToast: true,
      }),
    }),

    /** Masked status only — the backend never returns the raw number. */
    getPhoneStatus: builder.query<PhoneStatus, void>({
      query: () => ({
        url: "/v1/user/phone",
        method: "GET",
      }),
      transformResponse: (response: { success: boolean } & PhoneStatus): PhoneStatus => ({
        phoneMasked: response?.phoneMasked ?? null,
        phoneVerifiedAt: response?.phoneVerifiedAt ?? null,
      }),
      providesTags: ["SmsPhone"],
    }),

    /**
     * Sends a 6-digit code to the given E.164 number. The server enforces a
     * 60s resend cooldown and a daily cap; errors (INVALID_PHONE / COOLDOWN /
     * DAILY_CAP_REACHED / SMS_SEND_FAILED) are surfaced inline in the card.
     */
    startPhoneChallenge: builder.mutation<
      { success: boolean; error?: string; challengeId?: string; expiresAt?: string },
      { phone: string }
    >({
      query: (data) => ({
        url: "/v1/user/phone",
        method: "POST",
        data,
        suppressErrorToast: true,
      }),
    }),

    /**
     * Verifies the challenge code and records SMS consent. Invalidates the
     * preference matrix — SMS channel availability may change once verified.
     */
    verifyPhoneChallenge: builder.mutation<
      { success: boolean; error?: string; phoneMasked?: string },
      { code: string; uiContext?: string; consentTextVersion?: string }
    >({
      query: (data) => ({
        url: "/v1/user/phone/verify",
        method: "POST",
        data,
        suppressErrorToast: true,
      }),
      invalidatesTags: ["SmsPhone", "NotificationPreferences"],
    }),

    /** Removes the verified phone and revokes SMS consent. */
    removePhone: builder.mutation<{ success: boolean; error?: string }, void>({
      query: () => ({
        url: "/v1/user/phone",
        method: "DELETE",
        suppressErrorToast: true,
      }),
      invalidatesTags: ["SmsPhone", "NotificationPreferences"],
    }),
  }),
})

export const {
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
  useResetNotificationPreferencesMutation,
  useGetNotificationsQuery,
  useGetUnreadNotificationCountQuery,
  useMarkNotificationsReadMutation,
  useMarkAllNotificationsReadMutation,
  useUpdatePreferredLocaleMutation,
  useGetPhoneStatusQuery,
  useStartPhoneChallengeMutation,
  useVerifyPhoneChallengeMutation,
  useRemovePhoneMutation,
} = notificationsAPI

export default notificationsAPI
